import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAccountView, useSignInOpen } from '../hooks/useAuth';
import { usePresence } from '../hooks/usePresence';
import { identify, track } from '../lib/analytics';
import { AuthError, rememberPreference, safeReturnTo, setSignInOpen, signIn } from '../lib/auth';
import { strings } from '../lib/strings';
import { KeyraSignIn, SignupFlow } from './AccountFlows';
import { ActivityIndicator } from './ActivityIndicator';
import { AlertIcon, CloseIcon, EyeIcon, EyeOffIcon } from './Icons';
import { RollLabel } from './RollLabel';

const EXIT_MS = 320;

/** Keyra or Ciright sign-in and account creation, presented as an iOS sheet over whatever is on screen. */
export function SignInSheet() {
  const open = useSignInOpen();
  const view = useAccountView();
  const location = useLocation();
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const onCreated = useCallback((message: string) => {
    setNotice(message);
    setSignInOpen(true, 'sign-in');
  }, []);
  const { mounted, shown } = usePresence(open, EXIT_MS);
  const titleId = useId();
  const errorId = useId();
  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);
  const returnFocus = useRef<Element | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [remember, setRemember] = useState(rememberPreference);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(0);

  const close = () => {
    if (!busy) setSignInOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    // The content the visitor was on; they come back to it after signing in.
    setReturnTo(safeReturnTo(`${location.pathname}${location.search}`));
    setNotice(null);
    returnFocus.current = document.activeElement;
    setError(null);
    setPassword('');
    setReveal(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // An open pop-up menu takes Escape first.
      if ((e.target as Element | null)?.closest?.('[role="listbox"]')) return;
      e.stopPropagation();
      setSignInOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (returnFocus.current instanceof HTMLElement) returnFocus.current.focus({ preventScroll: true });
    };
    // Captures the destination once per presentation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && mounted && view === 'sign-in' && notice) {
      (username ? passRef : userRef).current?.focus({ preventScroll: true });
    }
    // Focus the password form only when signup just sent the visitor here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mounted, view, notice]);

  if (!mounted) return null;

  const canSubmit = username.trim().length > 0 && password.length > 0 && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const session = await signIn({ username, password, remember });
      identify(session.profile.uid);
      track('login_succeeded', { sign_in_method: 'ciright' });
      setPassword('');
      setSignInOpen(false);
    } catch (err) {
      track('login_failed', { sign_in_method: 'ciright', reason: err instanceof AuthError ? err.code : 'unexpected' });
      setError(err instanceof AuthError ? err.message : strings.account_.genericError);
      setShake((n) => n + 1);
      if (!(err instanceof AuthError) || err.code === 'credentials') {
        passRef.current?.select();
      }
    } finally {
      setBusy(false);
    }
  };

  const registering = view === 'register';
  const heading = registering
    ? { title: strings.account_.registerTitle, subtitle: strings.account_.registerSubtitle }
    : { title: strings.signInTitle, subtitle: strings.signInSubtitle };

  const passwordForm = (
    <form className="mt-4" onSubmit={submit} noValidate>
      <div key={shake} className={shake ? 'signin-shake' : undefined}>
        <div
          className={`signin-group overflow-hidden rounded-md bg-canvas ring-1 transition-shadow duration-fast ${
            error ? 'ring-[rgba(215,0,21,0.4)]' : 'ring-black/5'
          }`}
        >
          <label className="flex min-h-[50px] items-center gap-3 px-4">
            <span className="w-[84px] shrink-0 text-base text-ink">{strings.username}</span>
            <input
              ref={userRef}
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              value={username}
              disabled={busy}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => {
                setUsername(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !password) {
                  e.preventDefault();
                  passRef.current?.focus();
                }
              }}
              className="signin-input min-w-0 flex-1 bg-transparent py-3 text-lg text-ink placeholder:text-subtle"
              placeholder="name@company.com"
            />
          </label>
          <label className="relative flex min-h-[50px] items-center gap-3 pl-4 pr-2 before:absolute before:left-4 before:right-0 before:top-0 before:h-px before:bg-line">
            <span className="w-[84px] shrink-0 text-base text-ink">{strings.password}</span>
            <input
              ref={passRef}
              name="password"
              type={reveal ? 'text' : 'password'}
              autoComplete="current-password"
              enterKeyHint="go"
              value={password}
              disabled={busy}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              className="signin-input min-w-0 flex-1 bg-transparent py-3 text-lg text-ink placeholder:text-subtle"
              placeholder="Required"
            />
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? strings.hidePassword : strings.showPassword}
              aria-pressed={reveal}
              className="pressable flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full text-subtle hover:bg-fill hover:text-ink"
            >
              {reveal ? <EyeOffIcon className="h-[18px] w-[18px]" /> : <EyeIcon className="h-[18px] w-[18px]" />}
            </button>
          </label>
        </div>

        <div aria-live="assertive" className="min-h-[24px]">
          {error && (
            <p id={errorId} className="rise-in mt-2 flex items-start gap-2 px-1 text-sm font-medium text-danger">
              <AlertIcon className="mt-px h-4 w-4 shrink-0" />
              <span>{error}</span>
            </p>
          )}
        </div>
      </div>

      <label className="mt-2 flex min-h-[50px] cursor-pointer items-center justify-between gap-3 rounded-md bg-canvas px-4 ring-1 ring-black/5">
        <span className="text-base text-ink">{strings.keepSignedIn}</span>
        <input
          type="checkbox"
          role="switch"
          checked={remember}
          disabled={busy}
          onChange={(e) => setRemember(e.target.checked)}
          className="ios-switch"
        />
      </label>

      <button
        type="submit"
        disabled={!canSubmit}
        className="cta pressable mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {busy ? (
          <>
            <ActivityIndicator size="sm" />
            {strings.signingIn}
          </>
        ) : (
          <RollLabel>{strings.signIn}</RollLabel>
        )}
      </button>
    </form>
  );

  return createPortal(
    <div className="fixed inset-0 z-overlay flex items-end justify-center sm:items-center sm:p-4">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-scrim transition-opacity duration-slow ease-standard"
        style={{ opacity: shown ? 1 : 0 }}
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`share-panel relative max-h-[calc(100dvh-12px)] w-full overflow-y-auto overscroll-contain rounded-t-lg bg-material-thick px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-2 shadow-overlay backdrop-blur-2xl backdrop-saturate-150 sm:max-h-[calc(100dvh-32px)] sm:max-w-[420px] sm:rounded-lg sm:p-6 ${
          shown ? 'share-shown' : ''
        }`}
        style={{
          transition: shown
            ? 'transform 520ms var(--ease-sheet), opacity 220ms var(--ease-standard)'
            : `transform ${EXIT_MS}ms var(--ease-standard), opacity ${EXIT_MS}ms var(--ease-standard)`,
        }}
      >
        <span aria-hidden="true" className="mx-auto mb-3 block h-[5px] w-[36px] rounded-full bg-fill-strong sm:hidden" />
        <div className="flex justify-end">
          <button
            type="button"
            aria-label={strings.close}
            onClick={close}
            className="pressable flex h-[30px] w-[30px] items-center justify-center rounded-full bg-fill text-muted hover:bg-fill-strong hover:text-ink"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="-mt-3 flex flex-col items-center text-center">
          <span aria-hidden="true" className="signin-mark flex h-16 w-16 items-center justify-center text-white">
            <svg viewBox="0 0 40 40" className="h-10 w-10" fill="none">
              <circle cx="20" cy="20" r="8" fill="currentColor" />
              <ellipse
                cx="20"
                cy="20"
                rx="16.5"
                ry="6"
                transform="rotate(-24 20 20)"
                stroke="currentColor"
                strokeOpacity="0.55"
                strokeWidth="1.6"
              />
              <circle cx="33.4" cy="13.2" r="2.1" fill="var(--color-primary-bright)" />
            </svg>
          </span>
          <h2 id={titleId} className="mt-4 text-xl font-semibold tracking-tight text-ink">
            {heading.title}
          </h2>
          <p className="mt-1 text-sm text-muted">{heading.subtitle}</p>
        </div>

        {registering ? (
          <SignupFlow returnTo={returnTo} onCreated={onCreated} />
        ) : (
          <>
            {notice ? (
              <p role="status" className="rise-in mt-6 rounded-md bg-fill px-4 py-3 text-sm text-ink">
                {notice}
              </p>
            ) : (
              <KeyraSignIn returnTo={returnTo} />
            )}
            <div className="relative mt-6 flex items-center justify-center" aria-hidden="true">
              <span className="absolute inset-x-0 top-1/2 h-px bg-line" />
              <span className="relative bg-material-thick px-3 text-xs font-medium text-muted">{strings.account_.orCiright}</span>
            </div>
            {passwordForm}
            <p className="mt-4 text-center text-sm text-muted">
              {strings.account_.newHere}{' '}
              <button
                type="button"
                onClick={() => setSignInOpen(true, 'register')}
                className="pressable rounded-full px-1 font-semibold text-primary hover:bg-fill"
              >
                {strings.createAccount}
              </button>
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
